/*
  <collection-banners>
  Renders the collection banners configured in the LivGood admin.

  API: GET {api_base}/active?platform=web
  Response:
    { "success": true, "results": 2, "data": [ { id, banner, title, subtitle,
      link, collectionId, collectionHandle, collectionTitle, position } ] }

  Rules from the API brief:
    - Use `link` when non-empty (explicit admin override), otherwise build the
      url from `collectionHandle` -> /collections/<handle>.
    - `data` is already sorted by `position`; only live banners are returned.
    - `title` / `subtitle` are empty strings when unset, never null.
    - Empty `data` is a normal state — render nothing.
    - Fail open: on a network error or 5xx the section stays hidden.

  Below each banner it also renders that collection's title, a "see all" link
  and a grid of its products. The cards are the theme's real product cards:
  they are rendered by sections/helper-collection-banner-products.liquid and
  fetched through Shopify's section rendering API, so they match every other
  product grid on the site instead of being rebuilt in JavaScript.

  The collection's title comes back on that markup too, because the app API
  leaves collectionTitle empty in practice.

  The product rails load after the banners are already on screen, so a slow or
  deleted collection costs nothing: that banner simply appears without a rail.

  The whole section stays hidden until at least one banner renders, so an empty
  campaign never leaves a gap on the page.
*/
if (typeof customElements.get('collection-banners') === 'undefined') {

  const LG_CB_CACHE_KEY = 'lg_collection_promotions_v1';
  const LG_CB_CACHE_TTL = 5 * 60 * 1000; // 5 minutes — banners change rarely

  class CollectionBanners extends HTMLElement {

    connectedCallback() {
      this.rail = this.querySelector('[data-cb-rail]');
      this.apiBase = (this.dataset.apiBase || 'https://livgood-pt.herokuapp.com/api/v2/collection-promotion').replace(/\/+$/, '');
      this.limit = parseInt(this.dataset.limit, 10) || 12;

      this.showProducts = this.dataset.showProducts !== 'false';
      this.productsLimit = parseInt(this.dataset.productsLimit, 10) || 8;
      this.showViewAll = this.dataset.showViewAll !== 'false';
      this.viewAllLabel = this.dataset.viewAllLabel || 'See all';
      this.productsHeadingTag = this.dataset.productsHeadingTag || 'h3';
      this.productsLayout = this.dataset.productsLayout || 'grid-4 grid-portable-3 grid-lap-2 grid-tiny-1';
      this.productsStyle = this.dataset.productsStyle === 'grid' ? 'grid' : 'slider';
      this.sliderOptions = this.dataset.sliderOptions || '{"selector": ".product-item", "indexNav": true, "groupCells": true}';
      this.textBelow = this.dataset.textPosition !== 'overlay';

      if (!this.rail) return;
      this.load();
    }

    async load() {
      try {
        const banners = await this.fetchBanners();
        if (!banners.length) return;
        this.render(banners.slice(0, this.limit));
      } catch (e) {
        // Fail open — the page is never blocked by a banner campaign.
        console.warn('[collection-banners]', e);
      }
    }

    async fetchBanners() {
      // "Preview with sample banners" — the section ships the sample in the
      // API's own response shape, so everything downstream is the real path.
      if (this.dataset.sampleBanners) {
        try {
          const sample = JSON.parse(this.dataset.sampleBanners);
          if (Array.isArray(sample)) return sample;
        } catch (e) { /* fall through to the API */ }
      }

      const cached = this.readCache();
      if (cached) return cached;

      const res = await fetch(this.apiBase + '/active?platform=web', {
        headers: { 'Accept': 'application/json' }
      });
      if (!res.ok) return [];

      const json = await res.json();
      // Business failures come back as HTTP 200 with success:false.
      if (!json || json.success === false) return [];

      const data = Array.isArray(json.data) ? json.data : [];
      this.writeCache(data);
      return data;
    }

    readCache() {
      try {
        const raw = window.sessionStorage.getItem(LG_CB_CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (parsed && parsed.ts && (Date.now() - parsed.ts) < LG_CB_CACHE_TTL && Array.isArray(parsed.data)) {
          return parsed.data;
        }
      } catch (e) { /* ignore */ }
      return null;
    }

    writeCache(data) {
      try {
        window.sessionStorage.setItem(LG_CB_CACHE_KEY, JSON.stringify({ ts: Date.now(), data: data }));
      } catch (e) { /* storage full / disabled — ignore */ }
    }

    /* link wins when set, otherwise /collections/<handle>. */
    hrefFor(banner) {
      const link = String(banner.link || '').trim();
      // Only a real web url can be used as-is. The same admin field feeds the
      // mobile app, so it can hold a deep link like
      // `livgood://collection/promotions`, which does nothing in a browser.
      if (/^https?:\/\//i.test(link) || link.charAt(0) === '/') return link;

      let handle = String(banner.collectionHandle || '').trim();
      if (!handle && link) {
        const deepLink = link.match(/^[a-z][a-z0-9+.\-]*:\/\/collections?\/([^/?#]+)/i);
        if (deepLink) handle = decodeURIComponent(deepLink[1]);
      }
      // A label ("ultimate kid bundle") is not a handle; use the handle
      // Shopify would have made from it (loadProducts checks the id).
      if (handle && !/^[a-z0-9][a-z0-9_-]*$/.test(handle)) handle = CollectionBanners.handleize(handle);
      return handle ? '/collections/' + handle : '';
    }

    /*
      The handle used to fetch the products rail, which is not the same thing
      as the one used to build the link.

      `collectionHandle` is a free-text field in the LivGood admin, and what
      comes back in it is whatever someone typed: the live banner carries
      "prolife back to school giveaway" for a collection whose actual handle is
      `prolife-hero`. The link is the reliable field, because it is the one an
      admin pastes a real collection URL into — and the handle is sitting right
      there inside it.

      This failed silently for a while, and silently is the important part: a
      collection that does not exist still answers the section-rendering
      request with **200** and an empty rail, so there was nothing to catch.
      The banner rendered, the products just never appeared.

      So: take the handle out of the resolved href when there is one, and fall
      back to `collectionHandle` only when it actually looks like a handle.
      A value with spaces or capitals is a label, not a handle, and guessing a
      handle from it ("Prolife back to school giveaway" ->
      "prolife-back-to-school-giveaway") would only produce a different wrong
      url.
    */
    handleFor(banner, href) {
      const inHref = String(href || '').match(/\/collections\/([^/?#]+)/);
      if (inHref) return decodeURIComponent(inHref[1]);

      const raw = String(banner.collectionHandle || '').trim();
      if (/^[a-z0-9][a-z0-9_-]*$/.test(raw)) return raw;

      // A label rather than a handle ("ultimate kid bundle"). Shopify builds
      // handles from titles the same way, so this usually lands on the right
      // collection — and loadProducts() checks the collection it got against
      // the API's collectionId before showing any products, so a wrong guess
      // shows no products rather than the wrong ones.
      return CollectionBanners.handleize(raw);
    }

    static handleize(value) {
      return String(value || '')
        .toLowerCase()
        .normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
        .replace(/['’]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    }

    render(banners) {
      const frag = document.createDocumentFragment();
      const withProducts = [];
      let rendered = 0;

      banners.forEach((banner) => {
        const image = (banner.banner || '').trim();
        if (!image) return; // nothing to show

        const href = this.hrefFor(banner);
        const handle = this.handleFor(banner, href);

        // Each banner is its own block: the artwork, then the collection's
        // title and a "see all" link, then a rail of its products.
        const block = document.createElement('div');
        block.className = 'cb-block';

        const card = document.createElement(href ? 'a' : 'div');
        card.className = 'cb-card element--border-radius';
        if (href) {
          card.href = href;
          card.setAttribute('aria-label', banner.title || banner.collectionTitle || '');
        }

        const imgWrap = document.createElement('span');
        imgWrap.className = 'cb-card__image';

        const img = document.createElement('img');
        img.src = image;
        img.alt = banner.title || banner.collectionTitle || '';
        img.loading = 'lazy';
        img.decoding = 'async';
        imgWrap.appendChild(img);
        card.appendChild(imgWrap);

        const title = (banner.title || '').trim();
        const subtitle = (banner.subtitle || '').trim();

        if (title || subtitle) {
          const text = document.createElement('span');
          text.className = 'cb-card__text';

          if (title) {
            const t = document.createElement('span');
            t.className = 'cb-card__title';
            t.textContent = title;
            text.appendChild(t);
          }
          if (subtitle) {
            const s = document.createElement('span');
            s.className = 'cb-card__subtitle';
            s.textContent = subtitle;
            text.appendChild(s);
          }
          // Below the artwork, or laid over its bottom edge.
          if (this.textBelow) {
            text.classList.add('cb-card__text--below');
            block.appendChild(card);
            block.appendChild(text);
          } else {
            card.appendChild(text);
          }
        }

        if (!card.parentNode) block.appendChild(card);

        // The products rail is filled in asynchronously so a slow or missing
        // collection never holds up the banners themselves.
        if (this.showProducts && handle) {
          const products = document.createElement('div');
          products.className = 'cb-block__products';
          products.hidden = true;
          block.appendChild(products);
          withProducts.push({ banner: banner, handle: handle, href: href, node: products });
        }

        frag.appendChild(block);
        rendered += 1;
      });

      if (!rendered) return;

      this.rail.innerHTML = '';
      this.rail.appendChild(frag);
      this.classList.toggle('collection-banners--single', rendered === 1);
      this.classList.toggle('collection-banners--with-products', this.showProducts);
      this.removeAttribute('hidden');

      withProducts.forEach((entry) => this.loadProducts(entry));
    }
    /* --------------------------------------------------------------- products */

    /*
      The cards are the theme's own product cards, not a lookalike rebuilt
      here. sections/helper-collection-banner-products.liquid renders them, and
      Shopify's section rendering API gives that section the collection's
      context:

          /collections/<handle>?section_id=helper-collection-banner-products

      The markup comes back already styled by component-product-item.css, with
      the theme's lazy images, price formatting, badges and quick-buy form, so
      a banner rail is indistinguishable from a featured collection.

      The relative url matters: it inherits the current preview/theme session,
      the same way component-cart.js fetches '?section_id=helper-cart'.

      The collection's title and url ride along on the wrapper's data
      attributes, because the app API leaves collectionTitle empty in practice.
    */
    async loadProducts(entry) {
      try {
        const markup = await this.fetchSection(entry.handle);
        if (!markup) return;

        const wrapper = new DOMParser()
          .parseFromString(markup, 'text/html')
          .querySelector('[data-lg-cbp]');
        if (!wrapper) return;

        // Only the collection the API meant. A handle guessed from a label, or
        // a stale one, can resolve to a different collection (or to none: a
        // missing collection still answers 200 with an empty rail).
        const expectedId = String(entry.banner.collectionId || '').trim();
        const gotId = String(wrapper.dataset.collectionId || '').trim();
        if (expectedId && gotId && expectedId !== gotId) {
          console.warn('[collection-banners] ' + entry.handle + ' is collection ' + gotId + ', expected ' + expectedId);
          return;
        }

        const cards = Array.from(wrapper.children).slice(0, this.productsLimit);
        if (!cards.length) return;

        // With the banner's own title already under the artwork, a second
        // heading (the collection's) right below it would just repeat it.
        const bannerTitled = this.textBelow && (entry.banner.title || '').trim();
        const title = bannerTitled ? '' : ((entry.banner.collectionTitle || '').trim()
          || wrapper.dataset.collectionTitle || '');
        const url = wrapper.dataset.collectionUrl || entry.href;

        this.renderProducts(entry, title, url, cards);
      } catch (e) {
        // The banner stays; only the rail is missing.
        console.warn('[collection-banners] products', e);
      }
    }

    async fetchSection(handle) {
      const key = 'lg_cb_section_v2_' + handle + '_' + this.productsLimit;
      try {
        const cached = window.sessionStorage.getItem(key);
        if (cached) return cached;
      } catch (e) { /* ignore */ }

      const res = await fetch(
        '/collections/' + encodeURIComponent(handle) +
        '?section_id=helper-collection-banner-products',
        { headers: { 'Accept': 'text/html' } }
      );
      if (!res.ok) return '';

      const markup = await res.text();
      // Only worth keeping if it actually came back with cards. A missing
      // collection answers 200 with an empty rail, and caching that would keep
      // the rail empty for the rest of the session.
      if (markup.indexOf('data-lg-cbp') !== -1 && markup.indexOf('product-item') !== -1) {
        try {
          window.sessionStorage.setItem(key, markup);
        } catch (e) { /* storage full / disabled — ignore */ }
      }
      return markup;
    }

    renderProducts(entry, title, url, cards) {
      const node = entry.node;
      node.innerHTML = '';

      if (title || (this.showViewAll && url)) {
        const head = document.createElement('div');
        head.className = 'cb-block__head';

        if (title) {
          const heading = document.createElement(this.productsHeadingTag);
          heading.className = 'cb-block__title';
          heading.textContent = title;
          head.appendChild(heading);
        }

        if (this.showViewAll && url) {
          const viewAll = document.createElement('a');
          viewAll.className = 'cb-block__view-all text-link text-size--large';
          viewAll.href = url;
          viewAll.textContent = this.viewAllLabel;
          head.appendChild(viewAll);
        }

        node.appendChild(head);
      }

      /*
        Unhidden here, while it is still empty, rather than after it is filled.

        Two things need the rail to have a real box, and both of them happen
        during insertion. The slider measures itself to work out how many pages
        it has, and an element with no box measures 0 — that is what made it
        report one page for eight slides. And the theme's product cards carry
        native `loading="lazy"`, which the browser decides about at the moment
        each <img> is inserted; inserted into a `display: none` rail, that
        decision is made against no geometry at all.

        Unhiding an empty div costs nothing — it has no content yet, so it has
        no height — and from this line on everything that goes in is measured
        and evaluated against the real layout.
      */
      node.hidden = false;

      // The theme's own grid classes, so the cards lay out exactly as they do
      // in a featured collection.
      let slider = null;

      if (this.productsStyle === 'slider') {
        slider = this.buildSlider(cards);
        node.appendChild(slider);
      } else {
        const grid = document.createElement('div');
        grid.className = 'grid grid--layout ' + this.productsLayout;
        cards.forEach((card) => grid.appendChild(card));
        node.appendChild(grid);
      }

      if (slider) CollectionBanners.measureSlider(slider);
    }

    /*
      Wraps the cards in the theme's own <css-slider>, configured the same way
      the featured collection sections configure theirs, so the arrows, dots
      and swipe behaviour are the theme's rather than a second implementation.

      The whole thing is built as one markup string, deliberately. <css-slider>
      does all of its work in its constructor: it reads its options with
      JSON.parse(this.dataset.options) and it reads innerHTML to find its
      slides. A custom element upgrades as soon as it is created, so building it
      with createElement and filling it in afterwards runs that constructor
      against an element with no options and no cards — the JSON.parse throws,
      the element is left in the failed state, and it never becomes a slider.
      (Verified on the store: the element upgraded, kept its plain grid, and
      never gained arrows or dots.)

      Parsing markup that already carries both means the constructor sees a
      finished element, which is exactly the situation a server-rendered slider
      is in.
    */
    buildSlider(cards) {
      const html =
        '<css-slider class="css-slider css-slider--bottom-navigation" data-options="' +
          CollectionBanners.attr(this.sliderOptions) + '">' +
          '<div class="grid grid--slider ' + CollectionBanners.attr(this.productsLayout) + '">' +
            cards.map((card) => card.outerHTML).join('') +
          '</div>' +
        '</css-slider>';

      const holder = document.createElement('div');
      holder.innerHTML = html;
      return holder.firstElementChild;
    }

    /*
      <css-slider> works out how many pages it has by measuring its slides
      against its own width — in its constructor, which here runs while the
      markup is still detached, where every width is 0. It therefore concludes
      that everything fits on one page and hides its arrows and dots.

      One reset once it is on the page fixes that: the measurements are real,
      and 8 cards in a 4-up grid become the 2 pages they are. (Verified on the
      store: before the reset the slider reported 1 page for 8 slides with a
      2498px row in a 1238px viewport; after it, 2 pages, arrows and 2 dots.)

      The reset is synchronous — reading offsetWidth forces the layout it needs
      — and it runs only once the rail is unhidden, because a display:none
      element measures 0 just as a detached one does. An earlier version
      deferred it to requestAnimationFrame instead, which never runs while a
      tab is in the background, so a page opened in a background tab kept the
      one-page slider it was built with.

      The observer covers later width changes that the element would otherwise
      miss; it handles window resizes itself, but not its container being
      resized under it. It is deliberately not the primary path — resize
      observations are delivered on the rendering loop, which a background tab
      does not run either.
    */
    static measureSlider(slider) {
      const reset = () => {
        if (typeof slider.resetSlider === 'function') slider.resetSlider(true);
      };

      reset();

      if (typeof ResizeObserver !== 'function') return;

      let width = slider.offsetWidth;
      new ResizeObserver(() => {
        if (slider.offsetWidth === width) return;
        width = slider.offsetWidth;
        reset();
      }).observe(slider);
    }

    // Attribute-safe: these strings are theme settings, not shopper input, but
    // they are still going through innerHTML.
    static attr(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }
  }

  customElements.define('collection-banners', CollectionBanners);
}
